<template>
  <div>
    <b-row>
      <b-col cols="12" md="4">
        <b-form-group label="Grant level" label-class="small font-weight-bold">
          <v-select
            v-model="selectedLevel"
            :options="levelOptions"
            :reduce="option => option.value"
            :clearable="false"
            :disabled="disabled"
            placeholder="Choose a level"
          />
        </b-form-group>
      </b-col>

      <b-col cols="12" md="6">
        <b-form-group :label="currentLevelLabel" label-class="small font-weight-bold">
          <v-select
            v-model="pendingValue"
            :options="currentOptions"
            :reduce="option => option.value"
            :loading="loading"
            :disabled="disabled || !selectedLevel"
            placeholder="Search and select"
          />
        </b-form-group>
      </b-col>

      <b-col cols="12" md="2" class="d-flex align-items-center">
        <b-button
          variant="outline-primary"
          block
          :disabled="disabled || !pendingValue"
          @click="addGrant"
        >
          Add
        </b-button>
      </b-col>
    </b-row>

    <div v-if="!value.length" class="text-muted small mt-25">
      No locations granted yet. A restricted dimension with no values denies all access.
    </div>

    <div v-else class="mt-25">
      <b-badge
        v-for="grant in decodedGrants"
        :key="grant.token"
        variant="light-primary"
        class="mr-50 mb-50 py-50 px-1"
      >
        <span class="text-uppercase small mr-50">{{ grant.levelLabel }}</span>
        <span class="font-weight-bold">{{ grant.label }}</span>
        <feather-icon
          v-if="!disabled"
          icon="XIcon"
          size="14"
          class="ml-50 cursor-pointer"
          @click="removeGrant(grant.token)"
        />
      </b-badge>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash'
import vSelect from 'vue-select'
import {
  BRow,
  BCol,
  BBadge,
  BButton,
  BFormGroup,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import unwrapList from './unwrapList'
import locationLabel from './locationLabel'

/**
 * Grant values are stored as "<field>:<id>" tokens, matching the API's location-grant
 * parser. That is what lets a single client hold, say, two areas plus a whole mission
 * block — grants at different levels are OR'd on the read side.
 *
 * The two hierarchies are parallel:
 *   Domestic:  Region -> Area -> District -> Assembly
 *   Mission:   Mission Block -> RMC -> Country
 */
const LEVELS = [
  { value: 'region_id', label: 'Region', short: 'Region', hierarchy: 'domestic' },
  { value: 'area_id', label: 'Area', short: 'Area', hierarchy: 'domestic' },
  { value: 'district_id', label: 'District', short: 'District', hierarchy: 'domestic' },
  { value: 'assembly_id', label: 'Assembly', short: 'Assembly', hierarchy: 'domestic' },
  { value: 'mission_block_id', label: 'Mission Block', short: 'Block', hierarchy: 'mission' },
  { value: 'rmc_id', label: 'RMC', short: 'RMC', hierarchy: 'mission' },
  { value: 'country_id', label: 'Country', short: 'Country', hierarchy: 'mission' },
]

export default {
  components: {
    BRow,
    BCol,
    BBadge,
    BButton,
    BFormGroup,
    vSelect,
  },
  props: {
    // array of "<field>:<id>" tokens
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      selectedLevel: 'area_id',
      pendingValue: null,
      levelOptions: LEVELS.map(level => ({
        value: level.value,
        label: `${level.label} (${level.hierarchy === 'domestic' ? 'Domestic' : 'Mission'})`,
      })),
      // cache per level so switching back and forth does not refetch
      optionsByLevel: {},
    }
  },
  computed: {
    currentOptions() {
      return this.optionsByLevel[this.selectedLevel] || []
    },
    currentLevelLabel() {
      const level = LEVELS.find(entry => entry.value === this.selectedLevel)
      return level ? level.label : 'Value'
    },
    decodedGrants() {
      return this.value.map(token => {
        const separator = String(token).indexOf(':')
        // A bare id with no prefix is an area grant, matching the API's parser.
        const field = separator === -1 ? 'area_id' : String(token).slice(0, separator)
        const id = separator === -1 ? String(token) : String(token).slice(separator + 1)

        const level = LEVELS.find(entry => entry.value === field)
        const options = this.optionsByLevel[field] || []
        const match = options.find(option => option.value === id)

        return {
          token,
          field,
          id,
          levelLabel: level ? level.short : field,
          label: match ? match.label : id,
        }
      })
    },
  },
  watch: {
    selectedLevel: {
      handler(level) {
        this.pendingValue = null
        if (level) this.loadOptions(level)
      },
      immediate: true,
    },
  },
  created() {
    // Load the levels already granted so existing badges show names, not raw ids.
    const fields = new Set(this.decodedGrants.map(grant => grant.field))
    fields.forEach(field => this.loadOptions(field))
  },
  methods: {
    addGrant() {
      if (!this.pendingValue || !this.selectedLevel) return

      const token = `${this.selectedLevel}:${this.pendingValue}`
      if (this.value.includes(token)) {
        this.pendingValue = null
        return
      }

      this.$emit('input', [...this.value, token])
      this.pendingValue = null
    },
    removeGrant(token) {
      this.$emit('input', this.value.filter(entry => entry !== token))
    },
    async loadOptions(level) {
      if (this.optionsByLevel[level]) return

      try {
        this.loading = true

        const rows = await this.fetchLevel(level)
        const options = rows.map(row => ({
          value: row._id,
          label: locationLabel(row),
        }))

        this.$set(this.optionsByLevel, level, options)
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        })
      } finally {
        this.loading = false
      }
    },
    async fetchLevel(level) {
      const service = this.useJwt().sharedService
      const unwrap = unwrapList

      // A generous limit: these are reference collections in the tens to low hundreds,
      // and an admin picking a grant needs to see all of them, not page one.
      const params = { limit: 1000 }

      switch (level) {
        case 'region_id':
          return unwrap(await service.fetchGeoRegions())
        case 'area_id':
          return unwrap(await service.fetchGeoAreas(params))
        case 'district_id':
          return unwrap(await service.fetchGeoDistricts(params))
        case 'assembly_id':
          return unwrap(await service.fetchGeoAssemblies(params))
        case 'mission_block_id':
          return unwrap(await service.fetchMissionBlocks(params))
        case 'rmc_id':
          return unwrap(await service.fetchRmc(params))
        case 'country_id':
          // Un-paged like regions above: the route is `strict` and declares no `limit`,
          // so passing one is rejected rather than ignored.
          return unwrap(await service.fetchCountries())
        default:
          return []
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
